import { useCallback, useState } from "react";
import { notifications } from "@mantine/notifications";
import { normalizeIndianGstinInput } from "@shared/indianGstin";
import trpc from "@/shared/utils/trpc";
import {
  getTrpcErrorMessage,
  handleTrpcError,
  isIndiaGridlinesUserVerificationError,
} from "@/shared/utils/trpc/handletrpcError.util";

export const LAST_VERIFICATION_STORAGE_KEY = "my-profile:last-system-verification";

export type CompanyVerificationDetails = {
  legalName: string;
  headquartersCountry: string;
  state?: string | null;
};

function normalizeRegistrationNumber(
  payload: { registrationIdType: string; registrationNumber: string },
  hqCountry: string,
) {
  const hqTrim = hqCountry.trim();
  const usesLei = payload.registrationIdType === "LEI";
  const usesUsccShape =
    payload.registrationIdType === "USCC" ||
    (payload.registrationIdType === "COUNTRY_REGISTRATION" && hqTrim === "China (Mainland)");
  const usesGst = payload.registrationIdType === "GST";
  const usesDigitsOnlyType =
    payload.registrationIdType === "NOGN" || payload.registrationIdType === "SIREN";

  if (usesLei || usesUsccShape) return payload.registrationNumber.trim().toUpperCase();
  if (usesGst) return normalizeIndianGstinInput(payload.registrationNumber);
  if (usesDigitsOnlyType) return payload.registrationNumber.replace(/\D/g, "");
  return payload.registrationNumber.trim();
}

export function useApplyCompanyVerification(options: {
  companyId: string | undefined;
  companyDetails: CompanyVerificationDetails | null;
  /** Pass when verifying a facilitator-managed company (same APIs, scoped companyId). */
  managedCompanyId?: string;
  persistToLocalStorage?: boolean;
  onPendingReview?: () => void;
  onAfterSuccess?: () => void | Promise<void>;
  onApplyErrorClose?: () => void;
}) {
  const {
    companyId,
    companyDetails,
    managedCompanyId,
    persistToLocalStorage = true,
    onPendingReview,
    onAfterSuccess,
    onApplyErrorClose,
  } = options;

  const [verificationData, setVerificationData] = useState<Record<string, unknown> | null>(null);
  const [applying, setApplying] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const targetCompanyId = managedCompanyId ?? companyId;

  const apply = useCallback(
    async (payload: { registrationIdType: string; registrationNumber: string }) => {
      if (!targetCompanyId || !companyDetails) return;

      const hqCountry = companyDetails.headquartersCountry.trim();
      setSubmitError(null);
      setApplying(true);

      try {
        const normalizedRegistrationNumber = normalizeRegistrationNumber(payload, hqCountry);

        await trpc.company.update.mutate({
          companyId: targetCompanyId,
          data: {
            registrationIdType: payload.registrationIdType,
            registrationNumber: normalizedRegistrationNumber || null,
          },
        });

        const result = await trpc.verification.verifyCompany.mutate({
          ...(managedCompanyId ? { companyId: managedCompanyId } : {}),
          legalName: companyDetails.legalName,
          registrationNumber: normalizedRegistrationNumber,
          headquartersCountry: companyDetails.headquartersCountry,
          state: companyDetails.state || undefined,
        });

        if (result?.success && "data" in result && result.data) {
          const data = result.data as Record<string, unknown>;
          const nextVerificationData = {
            ...data,
            _verificationSource: (data.verificationSource as string | undefined) ?? result.source,
          };
          setVerificationData(nextVerificationData);
          if (persistToLocalStorage) {
            localStorage.setItem(
              LAST_VERIFICATION_STORAGE_KEY,
              JSON.stringify({
                companyId: targetCompanyId,
                data: nextVerificationData,
              }),
            );
          }
          await onAfterSuccess?.();
        } else if (result && !result.success && "pending24h" in result && result.pending24h) {
          notifications.show({
            title: "Verification pending admin review",
            message:
              (result as { message?: string }).message ||
              "Please wait up to 24 hours. Our team will review your registration. You will be notified when your profile is verified.",
            color: "blue",
          });
          await onAfterSuccess?.();
          onPendingReview?.();
        }
      } catch (err) {
        if (isIndiaGridlinesUserVerificationError(err, hqCountry, payload.registrationIdType)) {
          setSubmitError(getTrpcErrorMessage(err));
          return;
        }
        handleTrpcError(err);
        onApplyErrorClose?.();
      } finally {
        setApplying(false);
      }
    },
    [
      targetCompanyId,
      companyDetails,
      managedCompanyId,
      persistToLocalStorage,
      onAfterSuccess,
      onPendingReview,
      onApplyErrorClose,
    ],
  );

  const clearVerificationData = useCallback(() => {
    setVerificationData(null);
    setSubmitError(null);
  }, []);

  return {
    apply,
    applying,
    submitError,
    setSubmitError,
    verificationData,
    setVerificationData,
    clearVerificationData,
  };
}
